// FEAT-6 — which language a coding question is in, and which function a
// submission is called through. Browser twin of backend/grading/languages.py;
// the shared corpus in detect.test.ts and test_grader_dispatch.py keeps them
// equal. The RECRUITER sets the language per question, never the candidate.
// FEAT-12: the language list itself lives in registry.json (registry.ts).
import { CONTRACT, SUPPORTED_LANGUAGES, type Language } from "./registry";

export { SUPPORTED_LANGUAGES };
export type { Language };

/** The language of a question / snapshot. A missing or empty list (every
 *  question before FEAT-6) is Python; an unknown value also reads as Python
 *  here — the server reports it when grading. */
export function languageOf(allowed: readonly string[] | null | undefined): Language {
  const first = allowed?.[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(first ?? "")
    ? (first as Language)
    : "python";
}

const PY_DEF_RE = /def\s+([A-Za-z_][A-Za-z0-9_]*)\s*\(/;
// A function declaration, or a const/let/var bound to a function expression
// or an arrow. The right-hand side MUST be a function, so the cheat-proof
// header `const CHALLENGE_TOKEN = "…";` never matches.
// `\s*(?:\*\s*)?`, never `\s*\*?\s*` — the ambiguous spelling is quadratic on a
// long whitespace run after `function` (twin rule, languages.py).
const JS_DECL_RE = /\bfunction\s*(?:\*\s*)?([A-Za-z_$][\w$]*)\s*\(/;
const JS_BIND_RE =
  /\b(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s*=\s*(?:async\s+)?(?:function\b|\([^()]*\)\s*=>|[A-Za-z_$][\w$]*\s*=>)/;

function firstJsFunction(src: string): string | null {
  const hits = [JS_DECL_RE.exec(src), JS_BIND_RE.exec(src)].filter(
    (m): m is RegExpExecArray => m !== null,
  );
  if (hits.length === 0) return null;
  hits.sort((a, b) => a.index - b.index);
  return hits[0][1];
}

// TypeScript (FEAT-12 Build 2): the same two shapes, read off the RAW TypeScript
// header — generics after the name, typed parameters (one nested paren level),
// a return type before `=>` and a type on the binding itself. A type never
// crosses a newline or a `;` outside `{ … }`; `declare function` (erased by the
// type stripper) is skipped. Twin of languages.py's _TS_* regexes; detect.test.ts
// proves it finds what the JavaScript finder finds on sucrase's stripped output.
// Every repetition is BOUNDED, exactly as the twin (2026-09-27 pre-ship R2):
// unbounded, a long run of `const a:` starts is quadratic. The type run and the
// generics stay tight; parameter lists and bracket groups are sized for real
// starters (the twin's comment says why that stays linear).
const TS_TYPE = String.raw`(?:[^;{}\n]|\{[^{}]{0,400}\}){0,80}?`;
const TS_PARAMS = String.raw`\((?:[^()]|\([^()]{0,400}\)){0,2000}\)`;
const TS_GENERICS = String.raw`(?:<[^()]{0,80}?>\s*)?`;
const TS_DECL_RE = new RegExp(
  String.raw`\b(declare\s+)?function\s*(?:\*\s*)?([A-Za-z_$][\w$]*)\s*` + TS_GENERICS + String.raw`\(`,
  "g",
);
const TS_BIND_RE = new RegExp(
  String.raw`\b(?:const|let|var)\s+([A-Za-z_$][\w$]*)\s*(?::` +
    TS_TYPE +
    String.raw`)?=\s*(?:async\s+)?` +
    TS_GENERICS +
    String.raw`(?:function\b|` +
    TS_PARAMS +
    String.raw`\s*(?::` +
    TS_TYPE +
    String.raw`)?=>|[A-Za-z_$][\w$]*\s*=>)`,
);

function firstTsFunction(src: string): string | null {
  const decl = [...src.matchAll(TS_DECL_RE)].find((m) => !m[1]) ?? null;
  const bind = TS_BIND_RE.exec(src);
  const hits: { index: number; name: string }[] = [];
  if (decl) hits.push({ index: decl.index ?? 0, name: decl[2] });
  if (bind) hits.push({ index: bind.index, name: bind[1] });
  if (hits.length === 0) return null;
  hits.sort((a, b) => a.index - b.index);
  return hits[0].name;
}

function firstPyFunction(src: string): string | null {
  return PY_DEF_RE.exec(src)?.[1] ?? null;
}

/** The starter's function wins (a helper the candidate defines above it is
 *  never called by mistake), then the submission's first function, then
 *  `solution`. SQL and the program-mode languages (FEAT-12) have no function:
 *  the whole submission is the answer. */
export function detectFunctionName(
  language: Language,
  starterCode: string | null | undefined,
  userCode: string,
): string {
  if (CONTRACT[language] !== undefined && CONTRACT[language] !== "function") return "";
  const find =
    language === "javascript"
      ? firstJsFunction
      : language === "typescript"
        ? firstTsFunction
        : firstPyFunction;
  for (const src of [starterCode, userCode]) {
    if (src) {
      const name = find(src);
      if (name) return name;
    }
  }
  return "solution";
}
